import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useCallback, useMemo } from 'react'
import { setLoungeSurrogate } from '../../../actions/room'
import { setPinHelperID } from '../../../app-state/state.rooms'
import { isLounge as isRoomLounge } from '../../../mfcLogic/userProperties/user'
import { getModelColor, hexRGBA } from '../../../utilities/colors'
import { useUserSelector } from '../../Hooks/useUser'
import { getThemeSpacing } from '../../styles/appTheme'
import { palette } from '../../styles/colors'
import layers from '../../styles/layers'
import shape from '../../styles/shape'
import {
    hideDragHelper,
    highlightDragHelper,
    showDragHelper,
} from '../Chat/Rooms/PinnedRooms/PinDragHelper'
import PreviewPoster, { IPreviewPoster } from './PreviewPoster'

const ChatLivePreviewRoot = styled('div')({
    display: 'inline-flex',
    flexFlow: 'column nowrap',
    borderRadius: `${shape.borderRadius}px`,
    margin: '0 4px 0 4px',
    boxShadow: layers.one,
    position: 'relative',
    background: palette.backgroundPaper,
})
const PreviewPosterContainer = styled('div')({
    aspectRatio: '16/9',
    '& div': {
        borderBottomLeftRadius: shape.borderRadius,
        borderBottomRightRadius: shape.borderRadius,
    },
    '& img': {
        borderBottomLeftRadius: shape.borderRadius,
        borderBottomRightRadius: shape.borderRadius,
    },
})

const largePreviewRoot = css({
    wordWrap: 'break-word',
    overflow: 'clip',
    margin: 0,
    display: 'flex',
})

const largePreviewStyles = css({
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    '& div > img, & div > div': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
    },
})

const mymlBadgeStyles = css({
    rotate: '-45deg',
    position: 'absolute',
    bottom: 20,
    right: -20,
    background: palette.backgroundPaper,
    zIndex: 1,
    fontSize: '0.625rem',
    padding: '0 ' + getThemeSpacing(2),
})
const messageCss = css({
    width: '100%',
    textAlign: 'center',
    textShadow: 'none',
    fontSize: 13,
})

type LoungeChatPreviewProps = {
    fullWindow?: boolean
    darkMode?: boolean
    modelId: number
    loungeSlug: string
    onLoad?(event: any): void
    onClick?(id: number): void
    width?: number
    large?: boolean
    myml?: boolean
    // highlight?: boolean
    // message?: string
}

const LoungeChatPreview = memo((props: LoungeChatPreviewProps) => {
    const {
        onLoad,
        onClick,
        fullWindow = false,
        modelId,
        loungeSlug,
        darkMode = false,
        width = 128,
        large = false,
        myml = false,
        // highlight = false,
        // message,
    } = props

    const isTruePrivate = useUserSelector(modelId, 'isTruePrivate')
    const isBroadcasting = useUserSelector(modelId, 'isBroadcasting')
    const isPrivateShow = useUserSelector(modelId, 'isPrivateShow')
    const isNGVideo = useUserSelector(modelId, 'isNGVideo')
    const isGroupShow = useUserSelector(modelId, 'isGroupShow')
    const isClubShow = useUserSelector(modelId, 'isClubShow')
    const isOffline = useUserSelector(modelId, 'isOffline')
    const channel = useUserSelector(modelId, 'channel')
    const banned = useUserSelector(modelId, 'banned')
    const server = useUserSelector(modelId, 'server')
    const isLounge = useUserSelector(modelId, 'isLounge')
    const isModel = useUserSelector(modelId, 'isModel')
    const isAway = useUserSelector(modelId, 'isAway')
    const phase = useUserSelector(modelId, 'phase')
    const color = useUserSelector(modelId, 'color')
    const name = useUserSelector(modelId, 'name')

    const previewPosterProps: IPreviewPoster = {
        isTruePrivate,
        isBroadcasting,
        isPrivateShow,
        isGroupShow,
        isClubShow,
        isNGVideo,
        isOffline,
        isLounge,
        isModel,
        channel,
        isAway,
        banned,
        server,
        phase,
        id: modelId,
    }

    const modelColor = getModelColor(color, darkMode)

    const messageStyle = useMemo(
        () => ({
            background: !fullWindow
                ? hexRGBA(color ? color : '#000', 0.05)
                : hexRGBA(palette.background, 0.5),
            color: modelColor,
        }),
        [fullWindow, color, modelColor],
    )

    const onSetSurrogate = useCallback(() => {
        setLoungeSurrogate(modelId, loungeSlug)
        onClick?.(modelId)
    }, [modelId, loungeSlug, onClick])
    const dragStart = useCallback(
        (event) => {
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', modelId)
            showDragHelper(modelId)
        },
        [modelId],
    )
    const dragMove = useCallback((event) => {
        const { clientX, clientY } = event
        highlightDragHelper(clientX, clientY)
    }, [])
    const dragEnd = useCallback(() => {
        hideDragHelper()
        setPinHelperID(0)
    }, [])
    if (!isRoomLounge(loungeSlug)) return null

    return (
        <ChatLivePreviewRoot
            data-testid="Chat__LoungeChatPreview"
            onClick={onSetSurrogate}
            onDragStart={dragStart}
            onDrag={dragMove}
            onDragEnd={dragEnd}
            css={[
                large ? largePreviewRoot : null,
                large && !myml ? largePreviewStyles : null,
                // highlight && !large ? highlightPreview : null,
            ]}
            style={{ width: width }}
            id={`chat-preview-${modelId}`}
        >
            <a css={messageCss} style={messageStyle}>
                {name}
            </a>
            {myml && <span css={mymlBadgeStyles}>You May Like</span>}
            <PreviewPosterContainer>
                <PreviewPoster
                    {...previewPosterProps}
                    onLoad={onLoad}
                    setDimensions
                    width={width}
                    compact={!large}
                    small={!large}
                />
            </PreviewPosterContainer>
        </ChatLivePreviewRoot>
    )
})

export default LoungeChatPreview
